import { 
  ProviderMessageResult, 
  MessageStatus, 
  ProviderError,
  TemplateCreateResult,
  TemplateStatus,
  ProviderResponse 
} from '../contracts/provider.contract';

/**
 * Base response adapter for transforming provider responses to platform format
 */
export abstract class BaseResponseAdapter {
  /**
   * Transform provider message response to standard format
   */
  abstract transformMessageResponse(providerResponse: unknown): ProviderMessageResult;

  /**
   * Transform provider template response to standard format
   */
  abstract transformTemplateResponse(providerResponse: unknown): TemplateCreateResult;

  /**
   * Common error transformation
   */
  protected transformError(providerError: unknown): ProviderError {
    return {
      code: this.extractErrorCode(providerError),
      message: this.extractErrorMessage(providerError),
      details: this.extractErrorDetails(providerError)
    };
  }

  protected abstract extractErrorCode(providerError: unknown): string;
  protected abstract extractErrorMessage(providerError: unknown): string;
  protected abstract extractErrorDetails(providerError: unknown): Record<string, unknown>;

  /**
   * Common status mapping utilities
   */
  protected mapMessageStatus(providerStatus: string): MessageStatus {
    // Default implementation - override in specific adapters
    const statusMap: Record<string, MessageStatus> = {
      'queued': MessageStatus.QUEUED,
      'sending': MessageStatus.SENDING,
      'sent': MessageStatus.SENT,
      'delivered': MessageStatus.DELIVERED,
      'failed': MessageStatus.FAILED,
      'cancelled': MessageStatus.CANCELLED
    };

    return statusMap[providerStatus.toLowerCase()] || MessageStatus.FAILED;
  }

  protected mapTemplateStatus(providerStatus: string): TemplateStatus {
    // Default implementation - override in specific adapters
    const statusMap: Record<string, TemplateStatus> = {
      'draft': TemplateStatus.DRAFT,
      'pending': TemplateStatus.PENDING,
      'approved': TemplateStatus.APPROVED,
      'rejected': TemplateStatus.REJECTED,
      'disabled': TemplateStatus.DISABLED
    };

    return statusMap[providerStatus.toLowerCase()] || TemplateStatus.DRAFT;
  }

  protected parseDate(dateString: string): Date | undefined {
    if (!dateString) return undefined;
    
    try {
      return new Date(dateString);
    } catch {
      return undefined;
    }
  }
}

/**
 * Response adapter for IWINV provider
 */
export class IWINVResponseAdapter extends BaseResponseAdapter {
  transformMessageResponse(providerResponse: unknown): ProviderMessageResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      messageId: (response.msg_id || response.msgid) as string,
      status: this.mapIWINVMessageStatus(response.result_code as string),
      sentAt: this.parseDate(response.send_time as string),
      error: response.result_code !== '1' ? this.transformError(providerResponse) : undefined
    };
  }

  transformTemplateResponse(providerResponse: unknown): TemplateCreateResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      templateId: response.template_id as string,
      providerTemplateCode: response.template_code as string,
      status: this.mapIWINVTemplateStatus(response.status as string),
      message: (response.message || response.comment) as string
    };
  }

  private mapIWINVMessageStatus(resultCode: string): MessageStatus {
    const statusMap: Record<string, MessageStatus> = {
      '1': MessageStatus.SENT,
      '0': MessageStatus.FAILED,
      '-1': MessageStatus.FAILED,
      '-2': MessageStatus.FAILED,
      '-3': MessageStatus.FAILED,
      '-4': MessageStatus.FAILED
    };

    return statusMap[resultCode] || MessageStatus.FAILED;
  }

  private mapIWINVTemplateStatus(status: string): TemplateStatus {
    const statusMap: Record<string, TemplateStatus> = {
      'R': TemplateStatus.PENDING,      // Request
      'A': TemplateStatus.APPROVED,     // Approved
      'C': TemplateStatus.REJECTED,     // Cancelled/Rejected
      'S': TemplateStatus.PENDING       // Standby
    };

    return statusMap[status] || TemplateStatus.DRAFT;
  }

  protected extractErrorCode(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return (error.result_code || error.error_code || 'UNKNOWN_ERROR') as string;
  }

  protected extractErrorMessage(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return (error.message || error.error_message || 'Unknown error occurred') as string;
  }

  protected extractErrorDetails(providerError: unknown): Record<string, unknown> {
    const error = providerError as Record<string, unknown>;
    return {
      resultCode: error.result_code,
      originalResponse: providerError
    };
  }
}

/**
 * Response adapter for Aligo provider
 */
export class AligoResponseAdapter extends BaseResponseAdapter {
  transformMessageResponse(providerResponse: unknown): ProviderMessageResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      messageId: (response.msg_id || response.mid) as string,
      status: this.mapAligoMessageStatus(response.result_code as string),
      sentAt: this.parseDate(response.send_time as string),
      error: response.result_code !== '1' ? this.transformError(providerResponse) : undefined
    };
  }

  transformTemplateResponse(providerResponse: unknown): TemplateCreateResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      templateId: response.template_code as string,
      providerTemplateCode: response.template_code as string,
      status: this.mapAligoTemplateStatus(response.inspect_status as string),
      message: response.comment as string
    };
  }

  private mapAligoMessageStatus(resultCode: string): MessageStatus {
    const statusMap: Record<string, MessageStatus> = {
      '1': MessageStatus.SENT,
      '0': MessageStatus.FAILED,
      '-1': MessageStatus.FAILED,
      '-101': MessageStatus.FAILED,
      '-102': MessageStatus.FAILED
    };

    return statusMap[resultCode] || MessageStatus.FAILED;
  }

  private mapAligoTemplateStatus(inspectStatus: string): TemplateStatus {
    const statusMap: Record<string, TemplateStatus> = {
      'REG': TemplateStatus.PENDING,     // Registered
      'REQ': TemplateStatus.PENDING,     // Request
      'APR': TemplateStatus.APPROVED,    // Approved
      'REJ': TemplateStatus.REJECTED,    // Rejected
      'STOP': TemplateStatus.DISABLED    // Stopped
    };

    return statusMap[inspectStatus] || TemplateStatus.DRAFT;
  }

  protected extractErrorCode(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return (error.result_code || error.code || 'UNKNOWN_ERROR') as string;
  }

  protected extractErrorMessage(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return (error.message || error.error || 'Unknown error occurred') as string;
  }

  protected extractErrorDetails(providerError: unknown): Record<string, unknown> {
    const error = providerError as Record<string, unknown>;
    return {
      resultCode: error.result_code,
      inspectStatus: error.inspect_status,
      originalResponse: providerError
    };
  }
}

/**
 * Response adapter for Kakao provider (direct API)
 */
export class KakaoResponseAdapter extends BaseResponseAdapter {
  transformMessageResponse(providerResponse: unknown): ProviderMessageResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      messageId: response.message_id as string,
      status: this.mapKakaoMessageStatus(response.result_code as number),
      sentAt: this.parseDate(response.sent_time as string),
      error: response.result_code !== 0 ? this.transformError(providerResponse) : undefined
    };
  }

  transformTemplateResponse(providerResponse: unknown): TemplateCreateResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      templateId: response.template_id as string,
      providerTemplateCode: response.template_code as string,
      status: this.mapKakaoTemplateStatus(response.status as string),
      message: response.comments as string
    };
  }

  private mapKakaoMessageStatus(resultCode: number): MessageStatus {
    const statusMap: Record<number, MessageStatus> = {
      0: MessageStatus.SENT,
      [-1]: MessageStatus.FAILED,
      [-2]: MessageStatus.FAILED,
      [-3]: MessageStatus.FAILED,
      [-999]: MessageStatus.FAILED
    };

    return statusMap[resultCode] || MessageStatus.FAILED;
  }

  private mapKakaoTemplateStatus(status: string): TemplateStatus {
    const statusMap: Record<string, TemplateStatus> = {
      'TSC01': TemplateStatus.PENDING,    // Under review
      'TSC02': TemplateStatus.APPROVED,   // Approved
      'TSC03': TemplateStatus.REJECTED,   // Rejected
      'TSC04': TemplateStatus.DISABLED    // Disabled
    };

    return statusMap[status] || TemplateStatus.DRAFT;
  }

  protected extractErrorCode(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return String(error.result_code || error.error_code || 'UNKNOWN_ERROR');
  }

  protected extractErrorMessage(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return (error.message || error.error_message || 'Unknown error occurred') as string;
  }

  protected extractErrorDetails(providerError: unknown): Record<string, unknown> {
    const error = providerError as Record<string, unknown>;
    return {
      resultCode: error.result_code,
      originalResponse: providerError
    };
  }
}

/**
 * Response adapter for NHN provider
 */
export class NHNResponseAdapter extends BaseResponseAdapter {
  transformMessageResponse(providerResponse: unknown): ProviderMessageResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      messageId: response.requestId as string,
      status: this.mapNHNMessageStatus(response.statusCode as string),
      sentAt: this.parseDate(response.statusDateTime as string),
      error: response.statusCode !== 'SSS' ? this.transformError(providerResponse) : undefined
    };
  }

  transformTemplateResponse(providerResponse: unknown): TemplateCreateResult {
    const response = providerResponse as Record<string, unknown>;
    return {
      templateId: response.templateId as string,
      providerTemplateCode: response.templateId as string,
      status: this.mapNHNTemplateStatus(response.templateStatus as string),
      message: response.templateStatusName as string
    };
  }

  private mapNHNMessageStatus(statusCode: string): MessageStatus {
    const statusMap: Record<string, MessageStatus> = {
      'SSS': MessageStatus.SENT,          // Success
      'RDY': MessageStatus.QUEUED,        // Ready
      'PRG': MessageStatus.SENDING,       // Progress
      'CPL': MessageStatus.DELIVERED,     // Complete
      'FAL': MessageStatus.FAILED,        // Failed
      'CAL': MessageStatus.CANCELLED      // Cancelled
    };

    return statusMap[statusCode] || MessageStatus.FAILED;
  }

  private mapNHNTemplateStatus(templateStatus: string): TemplateStatus {
    const statusMap: Record<string, TemplateStatus> = {
      'WAITING': TemplateStatus.PENDING,
      'APPROVED': TemplateStatus.APPROVED,
      'REJECTED': TemplateStatus.REJECTED,
      'DISABLED': TemplateStatus.DISABLED
    };

    return statusMap[templateStatus] || TemplateStatus.DRAFT;
  }

  protected extractErrorCode(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return (error.statusCode || error.errorCode || 'UNKNOWN_ERROR') as string;
  }

  protected extractErrorMessage(providerError: unknown): string {
    const error = providerError as Record<string, unknown>;
    return (error.statusMessage || error.errorMessage || 'Unknown error occurred') as string;
  }

  protected extractErrorDetails(providerError: unknown): Record<string, unknown> {
    const error = providerError as Record<string, unknown>;
    return {
      statusCode: error.statusCode,
      statusMessage: error.statusMessage,
      originalResponse: providerError
    };
  }
}

/**
 * Factory for creating response adapters
 */
export class ResponseAdapterFactory {
  private static adapters: Map<string, new () => BaseResponseAdapter> = new Map<string, new () => BaseResponseAdapter>([
    ['iwinv', IWINVResponseAdapter],
    ['aligo', AligoResponseAdapter],
    ['kakao', KakaoResponseAdapter],
    ['nhn', NHNResponseAdapter]
  ]);

  static create(providerId: string): BaseResponseAdapter {
    const AdapterClass = this.adapters.get(providerId.toLowerCase());
    
    if (!AdapterClass) {
      throw new Error(`No response adapter found for provider: ${providerId}`);
    }
    
    return new AdapterClass();
  }

  static register(providerId: string, adapterClass: new () => BaseResponseAdapter): void {
    this.adapters.set(providerId.toLowerCase(), adapterClass);
  }
}